/*
 *
 * ReservationSearchBar is very coupled with the data types of the business page.
 * This component is intended to be generic but eventually gain parity with that and replace it.
 * Currently I only needed guest size so I implemented it as such.
 * - Corey (12-13-18)
 *
 */

import { LocalDate, LocalTime } from 'js-joda';
import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import clsx from 'clsx';

import { ACTION_EVENT_CLICK_CHOOSE_DATE_BUTTON } from 'utils/analytics/eventTypeConstantsAmplitude';
import { Button } from 'omakase';
import { useConsumerConversionAnalytics } from 'hooks/analytics/useConsumerConversionAnalytics';
import SearchBarGuestSection from 'components/business/search-bar/SearchBarGuestSection';

interface Props {
  className?: string;
  showGuestSelector?: boolean;
  showDateSelector?: boolean; // TODO: implement
  showTimeSelector?: boolean; // TODO: implement
  minGuestSize: number;
  maxGuestSize: number;
  onSubmit(size: number, date?: string, time?: string): void;
  partySizeUnit?: string;
  submitText?: string | React.ReactNode;
  submitButtonLabel?: string;
  submitButtonIcon?: React.ReactNode;
  isSubmitDisabled?: boolean;
}

// Calculate a default size, prioritizing the argument `defaultSize`
// but otherwise ensuring it is between the min and max
const calculateSelectedSizeDefault = (min: number, max: number, defaultSize = 1): number => {
  return Math.min(Math.max(defaultSize, min), max) || defaultSize;
};

const SimpleSearchBar: React.FC<Props> = ({
  className,
  showGuestSelector = true,
  showDateSelector = true,
  showTimeSelector = true,
  minGuestSize,
  maxGuestSize,
  onSubmit = _.noop,
  partySizeUnit,
  submitText = 'Search',
  submitButtonLabel = 'Search',
  submitButtonIcon,
  isSubmitDisabled,
}) => {
  const [selectedSize, setSelectedSize] = useState(1);
  const [selectedDate] = useState<LocalDate | undefined>();
  const [selectedTime] = useState<LocalTime | undefined>();

  const sendConversionAnalytics = useConsumerConversionAnalytics();

  useEffect(() => {
    const size = calculateSelectedSizeDefault(minGuestSize, maxGuestSize);
    setSelectedSize(size);
  }, [minGuestSize, maxGuestSize]);

  const handleSubmit = () => {
    onSubmit(selectedSize ?? minGuestSize, selectedDate?.toString(), selectedTime?.toString());
    sendConversionAnalytics(ACTION_EVENT_CLICK_CHOOSE_DATE_BUTTON);
  };

  const handleDecrementGuests = () => {
    const size = selectedSize - 1;
    setSelectedSize(size);
  };

  const handleIncrementGuests = () => {
    const size = selectedSize + 1;
    setSelectedSize(size);
  };

  const outerClassName = clsx('SimpleReservationSearchBar', className);

  return (
    <div className={outerClassName}>
      {showGuestSelector && (
        <div className="SimpleReservationSearchBar-input">
          <SearchBarGuestSection
            handleDecrementGuests={handleDecrementGuests}
            handleIncrementGuests={handleIncrementGuests}
            maxSize={maxGuestSize}
            minSize={minGuestSize}
            partySizeUnit={partySizeUnit}
            selectedSize={selectedSize}
            sizeClassName={''}
          />
        </div>
      )}

      <div className="SimpleReservationSearchBar-submit">
        <Button
          fullWidth
          variant="primary"
          size="large"
          iconStart={submitButtonIcon}
          aria-label={`${submitButtonLabel} (opens a dialog)`}
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          data-testid="reservations-search-submit"
        >
          {submitText}
        </Button>
      </div>
    </div>
  );
};

export default SimpleSearchBar;
